import { useDocumentTitle } from '../lib/useDocumentTitle';
import { LegalShell, Section, H } from './Privacy';

/**
 * Service terms — surfaced at /terms. Companion to /privacy. Linked
 * from the Signup checkbox. Covers what fans can / cannot do, IP rights,
 * disclaimer, governing law.
 */
export function Terms() {
  useDocumentTitle('服務條款 — NOXOR');
  const lastUpdated = '2026-05-22';

  return (
    <LegalShell title="服務條款" lastUpdated={lastUpdated}>
      <p>
        歡迎使用 NOXOR（「本服務」、「我們」）。當你註冊帳號、與 AI 角色聊天、
        或安裝 NOXOR Pet Chrome 擴充功能時，即代表你已閱讀並同意本條款。
        若不同意，請勿使用本服務。
      </p>

      <Section title="1. 服務內容">
        <p>
          NOXOR 是一個由 NOXOP Engine 驅動的 AI 角色互動平台。經營者
          （IP 持有人）可以在平台上部署 3D / 2D 角色，粉絲可以與這些角色聊天、
          收藏、累積好感度與點數。具體功能包含但不限於：
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>與 AI 角色文字 / 語音對話</li>
          <li>角色記憶系統與好感度追蹤</li>
          <li>點數錢包、儲值、購買角色皮膚 / 物件</li>
          <li>Chrome 擴充功能（桌寵浮窗、頁面摘要）</li>
        </ul>
      </Section>

      <Section title="2. 帳號註冊與使用者責任">
        <ul className="list-disc pl-5 space-y-1">
          <li>你必須年滿 13 歲；未成年人需家長或監護人同意。</li>
          <li>請提供真實的 email；密碼至少 8 字元。</li>
          <li>每位使用者僅限一個帳號。發現重複註冊將停權。</li>
          <li>你需自行保管帳號密碼。因密碼洩漏導致的損失由你自行負責。</li>
          <li>你同意不利用本服務從事任何違法、騷擾、仇恨、色情或惡意行為。</li>
        </ul>
      </Section>

      <Section title="3. 禁止行為">
        <p>使用本服務時，你不可：</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>嘗試破解、反向工程、繞過任何認證或計費機制。</li>
          <li>透過機器人、自動化腳本大量呼叫 API。</li>
          <li>上傳病毒、惡意程式碼，或干擾平台運作。</li>
          <li>抄襲、複製、轉售角色 IP、對話內容或平台素材。</li>
          <li>對 AI 角色說出鼓吹自殺、自我傷害、未成年色情或其他違法內容的訊息。</li>
          <li>試圖讓 AI 產生上述內容（包含 jailbreak、prompt injection 等手法）。</li>
        </ul>
        <p className="mt-2">
          違反者將被停權，必要時通報司法機關，並保留追究民事 / 刑事責任的權利。
        </p>
      </Section>

      <Section title="4. 點數、儲值與退款">
        <ul className="list-disc pl-5 space-y-1">
          <li>點數為虛擬資產，不具現金價值，不可兌換為法定貨幣。</li>
          <li>儲值的點數有效期限為購買日起 5 年；逾期未使用者作廢。</li>
          <li>除非平台關閉服務，否則已儲值的點數<b>不接受退款</b>。</li>
          <li>若因平台錯誤導致點數扣除，可申訴重新計算；經查屬實將補回。</li>
          <li>經營者可獨立設定其角色的點數消耗率；NOXOR 不負責經營者的定價爭議。</li>
        </ul>
      </Section>

      <Section title="5. 智慧財產權">
        <H>5.1 角色內容</H>
        <p>
          各角色的人設、外觀、聲音、台詞、IP 由其經營者擁有。你購買角色互動權
          僅取得個人使用授權，<b>不得擅自重製、散布、商業使用</b>。
        </p>
        <H>5.2 平台技術</H>
        <p>
          NOXOR 平台、NOXOP Engine、UI 設計、商標屬於 NOXOR 團隊所有。
        </p>
        <H>5.3 使用者產出</H>
        <p>
          你與 AI 對話產生的內容（你打的訊息 + AI 回應）著作權歸你所有，
          但你授權我們為了營運必要（記憶系統、摘要、安全分析）儲存與處理。
        </p>
      </Section>

      <Section title="6. AI 內容免責">
        <ul className="list-disc pl-5 space-y-1">
          <li>AI 角色的回應由 LLM 自動產生，可能包含<b>不準確、過時、虛構</b>的資訊。</li>
          <li>請勿將 AI 回應用於醫療、法律、財務等專業決策。</li>
          <li>AI 角色不是真人，也不替代心理諮商或情感支持服務。若你感到情緒困擾，請尋求專業協助（衛福部安心專線 1925）。</li>
        </ul>
      </Section>

      <Section title="7. 服務變更與終止">
        <ul className="list-disc pl-5 space-y-1">
          <li>我們保留隨時新增、修改、暫停或下架特定功能的權利。</li>
          <li>若需停止全部服務，將提前 30 天公告，並協助你下載資料 / 結算點數。</li>
          <li>違反本條款者，我們有權立即停權，並保留點數沒收的權利。</li>
        </ul>
      </Section>

      <Section title="8. 免責聲明">
        <p>
          本服務按「現狀」提供。在法律允許範圍內，NOXOR 不對因使用本服務造成的
          任何直接、間接、附隨損失負責，包含資料遺失、機會損失、情感損害等。
          本條款不排除我們依法應負的責任。
        </p>
      </Section>

      <Section title="9. 準據法與管轄">
        <p>
          本條款依台灣法律解釋與適用。因本服務產生之爭議，雙方合意以
          台灣台北地方法院為第一審管轄法院。
        </p>
      </Section>

      <Section title="10. 條款修改">
        <p>
          我們可能不定期修改本條款。重大變更會以站內公告或 email 通知，
          並於 14 天緩衝期後生效。你若繼續使用本服務即視為同意修改後的條款。
        </p>
      </Section>

      <Section title="11. 聯絡我們">
        <p>對本條款有疑問，請來信 <a href="mailto:sct@sparklecloud.co" className="underline">sct@sparklecloud.co</a>。</p>
      </Section>
    </LegalShell>
  );
}
